import type { FC, ReactNode } from 'react';
import React from 'react';
import { Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import type { PhotoBackgroundRendererProps } from './PhotoBackgroundRenderer.types';

export const PhotoBackgroundRenderer: FC<PhotoBackgroundRendererProps> = props => {
  const {
    photo,
    placeholderImageUrl,
    containerDimensions,
    height,
    isCompactView,
    renderedContainerHeight,
    renderedContainerWidth,
    isMobile,
    deviceSpecificContainerAspectRatio,
    photoRenderer: PhotoRenderer,
    minHeight = pxToRem(96),
    renderPhotoWrapper,
    ssrSizes,
    children,
  } = props;

  const containerWidth = renderedContainerWidth ?? containerDimensions.width;
  const containerHeight = renderedContainerHeight ?? containerDimensions.height;

  const derivedWidth = containerHeight * deviceSpecificContainerAspectRatio;
  const derivedHeight = containerWidth / deviceSpecificContainerAspectRatio;

  const { bestSuitableWidth, bestSuitableHeight } =
    derivedWidth < containerWidth
      ? { bestSuitableWidth: containerWidth, bestSuitableHeight: derivedHeight }
      : { bestSuitableWidth: derivedWidth, bestSuitableHeight: containerHeight };

  const cropInfoToUse = isMobile && photo?.mobileCropInfo ? photo.mobileCropInfo : photo?.cropInfo;

  const wrapPhoto = renderPhotoWrapper ?? ((el: ReactNode) => el);

  if (cropInfoToUse && photo) {
    const photoElement = (
      <Box
        position="absolute"
        top={isCompactView ? 0 : '50%'}
        left="50%"
        minWidth="100%"
        minHeight="100%"
        style={{
          aspectRatio: `${deviceSpecificContainerAspectRatio}`,
          transform: isCompactView ? 'translate(-50%, 0)' : 'translate(-50%, -50%)',
        }}
      >
        <PhotoRenderer
          photoUrl={photo.url}
          cropInfo={{ ...cropInfoToUse, unit: '%' }}
          width={isCompactView ? containerWidth : bestSuitableWidth}
          height={isCompactView ? derivedHeight : bestSuitableHeight}
          containerType="fixed-aspect"
          ssrSizes={ssrSizes}
        />
      </Box>
    );

    return (
      <Box
        width="100%"
        height="100%"
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
        >
          {wrapPhoto(photoElement)}
        </Box>
        {children}
      </Box>
    );
  }

  const backgroundImageUrl = photo?.url || placeholderImageUrl;

  const uncroppedPhoto = backgroundImageUrl ? (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
    >
      <PhotoRenderer
        photoUrl={backgroundImageUrl}
        width={bestSuitableWidth}
        height={bestSuitableHeight}
        containerType="viewport"
        containerAspectRatio={deviceSpecificContainerAspectRatio}
      />
    </Box>
  ) : null;

  return (
    <Box
      flexDirection="column"
      alignItems="center"
      width="100%"
      height={height}
      aspectRatio={isCompactView ? 'unset' : `${deviceSpecificContainerAspectRatio}`}
      overflow="hidden"
      minHeight={minHeight}
    >
      {uncroppedPhoto && wrapPhoto(uncroppedPhoto)}
      {children}
    </Box>
  );
};
